import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { patents, patentCategories, pctStrategy } from "../../mocks/patents";
import JsonLd from "../../components/base/JsonLd";

const patentsJsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "특허·지식재산 - 에너버즈(EnerVerze)",
  "description": "에너버즈의 핵심 기술력은 강력한 지식재산으로 보호됩니다. 국내외 특허 포트폴리오와 PCT 전략을 통해 글로벌 시장에서의 기술 경쟁력을 확보하고 있습니다.",
  "url": "https://enerverze.com/patents",
  "inLanguage": "ko-KR",
  "isPartOf": {
    "@type": "WebSite",
    "name": "에너버즈(EnerVerze)",
    "url": "https://enerverze.com/"
  },
  "about": {
    "@type": "Thing",
    "name": "특허 포트폴리오",
    "description": "AI Energy Platform, Digital Twin, Smart Grid, Distributed Energy 분야의 국내외 특허 및 PCT 출원"
  }
};

export default function PatentsPage() {
  return (
    <div className="min-h-screen bg-enerverze-bg">
      <JsonLd data={patentsJsonLd} />
      <Navbar />

      <main>
        {/* Page Header */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20patent%20and%20intellectual%20property%20concept%20with%20glowing%20document%20icons%20and%20shield%20symbols%20on%20dark%20slate%20background%20neon%20cyan%20and%20green%20accents%20futuristic%20tech%20aesthetic%20high%20quality%203D%20render%20minimalist%20design&width=1920&height=600&seq=patent-hero&orientation=landscape"
              alt="특허 배경"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-enerverze-bg/60 via-enerverze-bg/80 to-enerverze-bg"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-8 lg:px-12">
            <p className="text-sm text-neon-cyan mb-3">Patents & IP</p>
            <h1 className="text-4xl md:text-6xl font-bold text-enerverze-text leading-tight mb-4">
              특허·<span className="text-neon-cyan">지식재산</span>
            </h1>
            <p className="text-base md:text-lg text-enerverze-muted max-w-2xl">
              에너버즈의 핵심 기술력은 강력한 지식재산으로 보호됩니다. 
              국내외 특허 포트폴리오와 PCT 전략을 통해 글로벌 시장에서의 기술 경쟁력을 확보하고 있습니다.
            </p>
          </div>
        </section>

        {/* Patent Portfolio Table */}
        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-enerverze-text mb-2">
                특허 포트폴리오
              </h2>
              <p className="text-sm text-enerverze-muted">총 {patents.length}건의 특허를 보유하고 있습니다</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-enerverze-border">
                    <th className="py-4 pr-4 text-xs font-medium text-enerverze-subtle uppercase tracking-wider">출원번호</th>
                    <th className="py-4 pr-4 text-xs font-medium text-enerverze-subtle uppercase tracking-wider">발명의 명칭</th>
                    <th className="py-4 pr-4 text-xs font-medium text-enerverze-subtle uppercase tracking-wider">분야</th>
                    <th className="py-4 pr-4 text-xs font-medium text-enerverze-subtle uppercase tracking-wider">유형</th>
                    <th className="py-4 pr-4 text-xs font-medium text-enerverze-subtle uppercase tracking-wider">출원일</th>
                    <th className="py-4 text-xs font-medium text-enerverze-subtle uppercase tracking-wider">상태</th>
                  </tr>
                </thead>
                <tbody>
                  {patents.map((patent, idx) => (
                    <tr
                      key={idx}
                      className="border-b border-enerverze-border hover:bg-enerverze-card/50 transition-colors"
                    >
                      <td className="py-4 pr-4 text-sm text-enerverze-muted whitespace-nowrap">{patent.id}</td>
                      <td className="py-4 pr-4 text-sm text-enerverze-text">{patent.title}</td>
                      <td className="py-4 pr-4">
                        <span
                          className="inline-block px-2 py-1 rounded-md text-xs font-medium"
                          style={{
                            backgroundColor: patent.category === 'AI Energy Platform' ? 'rgba(34,211,238,0.15)' : 'rgba(52,211,153,0.15)',
                            color: patent.category === 'AI Energy Platform' ? '#22D3EE' : '#34D399',
                          }}
                        >
                          {patent.category}
                        </span>
                      </td>
                      <td className="py-4 pr-4 text-sm text-enerverze-muted">{patent.type}</td>
                      <td className="py-4 pr-4 text-sm text-enerverze-muted whitespace-nowrap">{patent.date}</td>
                      <td className="py-4">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                            patent.status === '등록완료'
                              ? 'bg-neon-green/20 text-neon-green'
                              : patent.status === '심사중'
                              ? 'bg-neon-cyan/20 text-neon-cyan'
                              : 'bg-enerverze-dim/30 text-enerverze-subtle'
                          }`}
                        >
                          {patent.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Patent Map */}
        <section className="py-16 md:py-24 bg-enerverze-card/30 border-y border-enerverze-border">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-enerverze-text mb-2">
                특허맵
              </h2>
              <p className="text-sm text-enerverze-muted">기술 분야별 특허 분포 현황</p>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {patentCategories.map((cat) => (
                <div
                  key={cat.name}
                  className="p-6 rounded-2xl bg-enerverze-card border border-enerverze-border hover:border-neon-cyan transition-all"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: cat.color }}
                    ></div>
                    <span className="text-sm font-medium text-enerverze-text">{cat.name}</span>
                  </div>
                  <p className="text-3xl font-bold text-enerverze-text mb-1">{cat.count}</p>
                  <p className="text-xs text-enerverze-subtle">등록·출원 건수</p>
                  <div className="mt-4 h-2 rounded-full bg-enerverze-border overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${(cat.count / 3) * 100}%`,
                        backgroundColor: cat.color,
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* PCT Strategy */}
        <section className="py-16 md:py-24">
          <div className="w-full px-4 md:px-8 lg:px-12">
            <div className="mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-enerverze-text mb-2">
                PCT <span className="text-neon-cyan">해외 출원 전략</span>
              </h2>
              <p className="text-sm text-enerverze-muted">
                글로벌 시장 진출을 위한 국제 특허 출원 로드맵
              </p>
            </div>

            <div className="space-y-4">
              {pctStrategy.map((item, idx) => (
                <div
                  key={idx}
                  className="flex flex-col md:flex-row md:items-center gap-4 p-6 rounded-2xl bg-enerverze-card border border-enerverze-border hover:border-neon-cyan transition-all"
                >
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-neon-cyan/10 border border-neon-cyan/30">
                      <span className="text-lg font-bold text-neon-cyan">{idx + 1}</span>
                    </span>
                  </div>
                  <div className="flex-grow">
                    <h3 className="text-lg font-semibold text-enerverze-text mb-1">{item.country}</h3>
                    <p className="text-sm text-enerverze-muted">{item.desc}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${
                        item.status === '진행중'
                          ? 'bg-neon-cyan/20 text-neon-cyan'
                          : item.status === '준비중'
                          ? 'bg-neon-green/20 text-neon-green'
                          : 'bg-enerverze-dim/30 text-enerverze-subtle'
                      }`}
                    >
                      {item.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}